// Benepack OMS · master api wrapper
// SSOT: api/openapi/oms-gateway.yaml /api/v1/master/*
//
// v0.1-frontend-rc2 (D-3.5 hotfix · Jason 2026-05-16):
//   - list/get/create/update response 改 CustomerListItem (gateway DTO 6 字段)
//   - 不是 Customer (proto schema · 11+ 字段)
//   - 跟 gateway v7.0.15/16 DTO 化决议 + shape 对称化对齐
//   - CreateCustomerRequest / UpdateCustomerRequest 仍是 proto 完整 form payload (POST/PUT body)
//
// v0.1-frontend-rc3 (D-4.1 · Jason 2026-05-17):
//   - 加 Supplier/Product/Sku/PriceList 4 entity × 5 endpoint adapter
//   - Create/Update 请求体: 按 backend proto raw 字段 (snake_case · "" / 0 表示 null)
//     ★ 重要 deviation: 跟 Customer rc2 wrapperspb { value: 'x' } 包装不一样
//        backend proto master.proto 4 entity Create/Update Request 全是 raw string/int (proto3)
//        不像 google.protobuf.{String,Int32}Value wrapper · 所以前端不能包 wrapperspb
//        否则 grpc-gateway unmarshal 会失败 ("cannot unmarshal object into ... type string")
//   - DELETE /master/<entity>/{id}:
//     * Supplier → DisableSupplier (语义 disable)
//     * Product → DisableProduct
//     * Sku → DisableSku
//     * PriceList → DeprecatePriceList (语义 deprecate · 不是 disable · status=deprecated)
//   - List query string filter (kind/customer_id/supplier_id/status) gateway 一期未解析
//     · 仅 ?page=1&size=20 通 · 推 v7.4.x backend 微迭代
//
// v0.1-frontend-rc3.2 (D-4.1 form create 2 P0 hotfix · Jason 2026-05-24):
//   - P0-A: Customer 也去 wrapperspb · CreateCustomerRequest 改 raw string/int
//           → rc3 的 "Customer rc2 wrapperspb deviation" 已消除 · 5 entity 全 raw 统一
//   - P0-B: 5 entity create/update 提交前 enum (kind/status) short-name → proto 枚举值名 (adapter 层)
//
// v0.1-frontend-rc8 (WP-06 · v7.5.0 protojson 归一 · Jason 2026-05-29):
//   - gateway DecodeJSON 已换 protojson (commit 199108b) · enum 字段既认 int 也认 proto 枚举值名 string。
//   - enum-codec 由 "短名 → int" stopgap 归一为 "短名 → proto 枚举值名 string" (canonical protojson
//     形态 · 不再依赖魔法数字)。form/zod 内部仍保持短名 string (UI 友好) · 提交前 adapter 映射。
//     codec 仍需保留: protojson 不认前端短名 ("active") · 只认 proto 值名/int。
//     SSOT: memory oms_v7_5_0_protojson_done.md + gateway_request_json_enum_int_only.md
import { http } from './client';
import {
  ContactOwnerKindToProto,
  ContactStatusToProto,
  CustomerKindToProto,
  CustomerStatusToProto,
  SupplierKindToProto,
  SupplierStatusToProto,
  ProductStatusToProto,
  SkuStatusToProto,
  PriceListKindToProto,
  PriceListStatusToProto,
  DiscountTypeToProto,
  CustomerDiscountStatusToProto,
  mapEnum,
  mapEnumOpt,
} from './enum-codec';
import type {
  CustomerKind,
  CustomerStatus,
  SupplierKind,
  SupplierStatus,
  ProductStatus,
  SKUStatus,
  PriceListKind,
  PriceListStatus,
  DiscountType,
  CustomerDiscountStatus,
} from '@/types/master';
import type {
  CustomerListItem,
  CustomerFullDetail,
  SupplierListItem,
  SupplierFullDetail,
  ProductListItem,
  SkuListItem,
  PriceListListItem,
  PriceQuoteView,
  CustomerDiscountListItem,
  BatchListItem,
  CoaReportListItem,
  ContactPersonListItem,
  ListWrap,
} from '@/types/views';

/**
 * buildUpdateMask · P0-2 部分更新：把表单"脏字段"(snake_case · zod/RHF 字段名) 转成
 * protojson google.protobuf.FieldMask 形态——lowerCamelCase path 逗号串。
 *
 * ★ 必须 camelCase：gateway protojson 解码 FieldMask 只认 lowerCamelCase path（snake 串会
 *   被判 "invalid path" 400），解码后 Paths 自动还原 snake_case 交 master usecase。
 *   例：["name","credit_limit","payment_terms_days"] → "name,creditLimit,paymentTermsDays"。
 */
export function buildUpdateMask(dirtyFieldNames: string[]): string {
  return dirtyFieldNames
    .map((f) => f.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()))
    .join(',');
}

// =========== Customer ===========

export interface ListCustomersRequest {
  page?: number;
  size?: number;
  q?: string;
  status?: CustomerStatus | CustomerStatus[];
  kind?: CustomerKind | CustomerKind[];
  sales_user_id?: string;
  sort?: string; // e.g. "-created_at"
}

/**
 * ListCustomersResponse · gateway 真返 shape
 * SSOT: apps/oms-gateway/internal/master_client/mapper.go:CustomerListView
 * shape: { items: CustomerListItem[], page: PageView }
 */
export type ListCustomersResponse = ListWrap<CustomerListItem>;

/**
 * CreateCustomerRequest · POST /master/customers 请求体
 * SSOT: api/proto/master/v1/master.proto:CreateCustomerRequest
 *
 * ★ v0.1-frontend-rc3.2 (P0-A · Jason 2026-05-24): 去 wrapperspb · 全 raw string/int
 *    backend proto CreateCustomerRequest 字段全是 raw string/int (proto3 · "" / 0 = NULL)
 *    · 没用 google.protobuf.{String,Int32}Value wrapper
 *    · rc2 误用 { value: 'x' } 包装 → gateway 标准 json 拒 object ("cannot unmarshal object into ... type string" · 400)
 *    · 对齐 CreateSupplierRequest raw 范式 (4 entity 全 raw)
 *    enum (kind/status) UI/form 内仍是短名 string · 提交前由 adapter (createCustomer) 转 proto int
 *
 * 含敏感字段 (sales_user_id / credit_limit / payment_terms) · 不在 gateway ACL 剔除范围 (form 提交侧)
 * 返 CustomerListItem (DTO 化精简 · 跟 GET single 同款 shape)
 */
export interface CreateCustomerRequest {
  code: string;
  name: string;
  short_name?: string;
  kind: CustomerKind;
  unified_credit_no?: string;
  tax_no?: string;
  status: CustomerStatus;
  credit_limit: string; // numeric(20,4) 走 string
  payment_terms?: string;
  payment_terms_days?: number | null; // per-customer 账期天数 (GL-P0) · null/留空 = 全局默认 (proto3 null→0→未配置)
  default_currency: string;
  sales_user_id?: number; // int64 · 0 = NULL (form 暂无输入)
  // 开票信息 (开票六要素 + 邮箱) · 明文选填 · "" = NULL (proto3 raw string)
  invoice_title?: string;
  invoice_address?: string;
  invoice_phone?: string;
  bank_name?: string;
  bank_account?: string;
  invoice_email?: string;
  remark?: string;
}

export type UpdateCustomerRequest = Partial<CreateCustomerRequest>;

export async function listCustomers(req: ListCustomersRequest): Promise<ListCustomersResponse> {
  const resp = await http.get<ListCustomersResponse>('/api/v1/master/customers', { params: req });
  return resp.data;
}

export async function getCustomer(id: string | number): Promise<CustomerListItem> {
  const resp = await http.get<CustomerListItem>(`/api/v1/master/customers/${id}`);
  return resp.data;
}

/**
 * getCustomerFullDetail · P0-2 编辑表单全字段读 (GET /customers/{id}/full)。
 * 返回含敏感字段 (credit_limit / payment_terms / sales_user_id / remark 等) 的完整可编辑视图，
 * 供编辑表单 prefill 全字段——避免提交时以空值覆盖原值。详情/列表仍走精简 getCustomer。
 */
export async function getCustomerFullDetail(id: string | number): Promise<CustomerFullDetail> {
  const resp = await http.get<CustomerFullDetail>(`/api/v1/master/customers/${id}/full`);
  return resp.data;
}

export async function createCustomer(req: CreateCustomerRequest): Promise<CustomerListItem> {
  const wire = {
    ...req,
    kind: mapEnum(CustomerKindToProto, req.kind),
    status: mapEnum(CustomerStatusToProto, req.status),
  };
  const resp = await http.post<CustomerListItem>('/api/v1/master/customers', wire);
  return resp.data;
}

/**
 * updateCustomer · P0-2 部分更新。
 * @param updateMaskPaths 表单脏字段名 (snake_case)。**非空时**随请求下发 update_mask，
 *   后端仅更新这些字段、其余保留库中原值（防未 prefill/未改动字段被空值覆盖）。
 *   缺省/空 → 不带 mask = 全字段覆盖（向后兼容·调用方须确保已 prefill 全字段）。
 */
export async function updateCustomer(
  id: string | number,
  req: UpdateCustomerRequest,
  updateMaskPaths?: string[],
): Promise<CustomerListItem> {
  const wire: Record<string, unknown> = {
    ...req,
    kind: mapEnumOpt(CustomerKindToProto, req.kind),
    status: mapEnumOpt(CustomerStatusToProto, req.status),
  };
  if (updateMaskPaths && updateMaskPaths.length > 0) {
    wire.update_mask = buildUpdateMask(updateMaskPaths);
  }
  const resp = await http.put<CustomerListItem>(`/api/v1/master/customers/${id}`, wire);
  return resp.data;
}

export async function deleteCustomer(id: string | number): Promise<void> {
  await http.delete(`/api/v1/master/customers/${id}`);
}

// =========== Supplier (D-4.1 · 5 endpoint) ===========

export interface ListSuppliersRequest {
  page?: number;
  size?: number;
  q?: string;
  status?: SupplierStatus | SupplierStatus[];
  kind?: SupplierKind | SupplierKind[];
  sort?: string;
}

export type ListSuppliersResponse = ListWrap<SupplierListItem>;

/**
 * CreateSupplierRequest · POST /master/suppliers 请求体
 * SSOT: api/proto/master/v1/master.proto:CreateSupplierRequest
 * 字段全 raw string/int (proto3 · 非 wrapperspb · "" / 0 表示 null)
 */
export interface CreateSupplierRequest {
  code: string;
  name: string;
  short_name?: string;
  kind: SupplierKind;
  unified_credit_no?: string;
  status: SupplierStatus;
  default_currency: string;
  default_payment_terms?: string;
  lead_time_days?: number; // 0 = NULL
  payment_terms_days?: number | null; // per-supplier 账期天数 (GL-P0) · null/留空 = 全局默认 (proto3 null→0→未配置)
  // 开票信息 (开票六要素 + 邮箱) · 明文选填 · "" = NULL (proto3 raw string)
  invoice_title?: string;
  invoice_address?: string;
  invoice_phone?: string;
  bank_name?: string;
  bank_account?: string;
  invoice_email?: string;
  remark?: string;
}

export type UpdateSupplierRequest = Partial<CreateSupplierRequest>;

export async function listSuppliers(req: ListSuppliersRequest): Promise<ListSuppliersResponse> {
  const resp = await http.get<ListSuppliersResponse>('/api/v1/master/suppliers', { params: req });
  return resp.data;
}

export async function getSupplier(id: string | number): Promise<SupplierListItem> {
  const resp = await http.get<SupplierListItem>(`/api/v1/master/suppliers/${id}`);
  return resp.data;
}

/**
 * getSupplierFullDetail · P0-2 编辑表单全字段读 (GET /suppliers/{id}/full)。镜像 getCustomerFullDetail。
 */
export async function getSupplierFullDetail(id: string | number): Promise<SupplierFullDetail> {
  const resp = await http.get<SupplierFullDetail>(`/api/v1/master/suppliers/${id}/full`);
  return resp.data;
}

export async function createSupplier(req: CreateSupplierRequest): Promise<SupplierListItem> {
  const wire = {
    ...req,
    kind: mapEnum(SupplierKindToProto, req.kind),
    status: mapEnum(SupplierStatusToProto, req.status),
  };
  const resp = await http.post<SupplierListItem>('/api/v1/master/suppliers', wire);
  return resp.data;
}

/**
 * updateSupplier · P0-2 部分更新 (镜像 updateCustomer)。updateMaskPaths 非空时下发 update_mask。
 */
export async function updateSupplier(
  id: string | number,
  req: UpdateSupplierRequest,
  updateMaskPaths?: string[],
): Promise<SupplierListItem> {
  const wire: Record<string, unknown> = {
    ...req,
    kind: mapEnumOpt(SupplierKindToProto, req.kind),
    status: mapEnumOpt(SupplierStatusToProto, req.status),
  };
  if (updateMaskPaths && updateMaskPaths.length > 0) {
    wire.update_mask = buildUpdateMask(updateMaskPaths);
  }
  const resp = await http.put<SupplierListItem>(`/api/v1/master/suppliers/${id}`, wire);
  return resp.data;
}

export async function disableSupplier(id: string | number): Promise<void> {
  await http.delete(`/api/v1/master/suppliers/${id}`);
}

// =========== Product (D-4.1 · 5 endpoint) ===========

export interface ListProductsRequest {
  page?: number;
  size?: number;
  q?: string;
  status?: ProductStatus | ProductStatus[];
  sort?: string;
}

export type ListProductsResponse = ListWrap<ProductListItem>;

/**
 * CreateProductRequest · POST /master/products 请求体
 * SSOT: api/proto/master/v1/master.proto:CreateProductRequest (6 字段)
 */
export interface CreateProductRequest {
  code: string;
  name: string;
  category?: string;
  regulatory_no?: string;
  description?: string;
  status: ProductStatus;
}

export type UpdateProductRequest = Partial<CreateProductRequest>;

export async function listProducts(req: ListProductsRequest): Promise<ListProductsResponse> {
  const resp = await http.get<ListProductsResponse>('/api/v1/master/products', { params: req });
  return resp.data;
}

export async function getProduct(id: string | number): Promise<ProductListItem> {
  const resp = await http.get<ProductListItem>(`/api/v1/master/products/${id}`);
  return resp.data;
}

export async function createProduct(req: CreateProductRequest): Promise<ProductListItem> {
  const wire = { ...req, status: mapEnum(ProductStatusToProto, req.status) };
  const resp = await http.post<ProductListItem>('/api/v1/master/products', wire);
  return resp.data;
}

export async function updateProduct(
  id: string | number,
  req: UpdateProductRequest,
): Promise<ProductListItem> {
  const wire = { ...req, status: mapEnumOpt(ProductStatusToProto, req.status) };
  const resp = await http.put<ProductListItem>(`/api/v1/master/products/${id}`, wire);
  return resp.data;
}

export async function disableProduct(id: string | number): Promise<void> {
  await http.delete(`/api/v1/master/products/${id}`);
}

// =========== SKU (D-4.1 · 5 endpoint) ===========

export interface ListSkusRequest {
  page?: number;
  size?: number;
  q?: string;
  product_id?: number;
  status?: SKUStatus | SKUStatus[];
  sort?: string;
}

export type ListSkusResponse = ListWrap<SkuListItem>;

/**
 * CreateSkuRequest · POST /master/skus 请求体
 * SSOT: api/proto/master/v1/master.proto:CreateSkuRequest (8 字段)
 */
export interface CreateSkuRequest {
  product_id: number;
  code: string;
  spec: string;
  uom: string;
  barcode?: string;
  default_supplier_id?: number; // 0 = NULL
  default_unit_price?: string; // "" = NULL · numeric(18,4)
  status: SKUStatus;
}

export type UpdateSkuRequest = Partial<CreateSkuRequest>;

export async function listSkus(req: ListSkusRequest): Promise<ListSkusResponse> {
  const resp = await http.get<ListSkusResponse>('/api/v1/master/skus', { params: req });
  return resp.data;
}

export async function getSku(id: string | number): Promise<SkuListItem> {
  const resp = await http.get<SkuListItem>(`/api/v1/master/skus/${id}`);
  return resp.data;
}

export async function createSku(req: CreateSkuRequest): Promise<SkuListItem> {
  const wire = { ...req, status: mapEnum(SkuStatusToProto, req.status) };
  const resp = await http.post<SkuListItem>('/api/v1/master/skus', wire);
  return resp.data;
}

export async function updateSku(id: string | number, req: UpdateSkuRequest): Promise<SkuListItem> {
  const wire = { ...req, status: mapEnumOpt(SkuStatusToProto, req.status) };
  const resp = await http.put<SkuListItem>(`/api/v1/master/skus/${id}`, wire);
  return resp.data;
}

export async function disableSku(id: string | number): Promise<void> {
  await http.delete(`/api/v1/master/skus/${id}`);
}

// =========== PriceList (D-4.1 · 5 endpoint · DELETE 语义 Deprecate) ===========

export interface ListPriceListsRequest {
  page?: number;
  size?: number;
  q?: string;
  kind?: PriceListKind | PriceListKind[];
  customer_id?: number;
  supplier_id?: number;
  status?: PriceListStatus | PriceListStatus[];
  sort?: string;
}

export type ListPriceListsResponse = ListWrap<PriceListListItem>;

/**
 * CreatePriceListRequest · POST /master/price-lists 请求体
 * SSOT: api/proto/master/v1/master.proto:CreatePriceListRequest (9 字段)
 *
 * 字段约束 (跟 backend proto):
 *   - customer_id 0 = NULL (kind=sales 通用价表)
 *   - supplier_id 0 = NULL (kind=purchase 通用价表)
 *   - valid_from "YYYY-MM-DD" 必填
 *   - valid_to "" = 无截止 · 可空
 *   - status UNSPECIFIED = 默认 draft (前端最好显式传)
 */
export interface CreatePriceListRequest {
  code: string;
  name: string;
  kind: PriceListKind;
  currency: string;
  customer_id?: number; // 0 = NULL
  supplier_id?: number; // 0 = NULL
  valid_from: string; // "YYYY-MM-DD"
  valid_to?: string; // "" = 无截止
  status: PriceListStatus;
}

export type UpdatePriceListRequest = Partial<CreatePriceListRequest>;

export async function listPriceLists(req: ListPriceListsRequest): Promise<ListPriceListsResponse> {
  const resp = await http.get<ListPriceListsResponse>('/api/v1/master/price-lists', {
    params: req,
  });
  return resp.data;
}

export async function getPriceList(id: string | number): Promise<PriceListListItem> {
  const resp = await http.get<PriceListListItem>(`/api/v1/master/price-lists/${id}`);
  return resp.data;
}

export async function createPriceList(req: CreatePriceListRequest): Promise<PriceListListItem> {
  const wire = {
    ...req,
    kind: mapEnum(PriceListKindToProto, req.kind),
    status: mapEnum(PriceListStatusToProto, req.status),
  };
  const resp = await http.post<PriceListListItem>('/api/v1/master/price-lists', wire);
  return resp.data;
}

export async function updatePriceList(
  id: string | number,
  req: UpdatePriceListRequest,
): Promise<PriceListListItem> {
  const wire = {
    ...req,
    kind: mapEnumOpt(PriceListKindToProto, req.kind),
    status: mapEnumOpt(PriceListStatusToProto, req.status),
  };
  const resp = await http.put<PriceListListItem>(`/api/v1/master/price-lists/${id}`, wire);
  return resp.data;
}

/**
 * DeprecatePriceList · DELETE /master/price-lists/{id}
 * 语义: terminal soft-stop · backend status → 'deprecated' (不是 'disabled')
 * 跟 Customer/Supplier/Product/Sku 的 Disable 语义不同 · 一致 HTTP method DELETE
 */
export async function deprecatePriceList(id: string | number): Promise<void> {
  await http.delete(`/api/v1/master/price-lists/${id}`);
}

// =========== Batch (v0.1 batch-slice · 3 endpoint · list/get/create · 无 update/disable) ===========

export interface ListBatchesRequest {
  page?: number;
  size?: number;
  supplier_id?: number;
  sku_id?: number;
}

export type ListBatchesResponse = ListWrap<BatchListItem>;

/**
 * CreateBatchRequest · POST /master/batches 请求体
 * SSOT: api/proto/master/v1/master.proto:CreateBatchRequest (8 字段 · raw)
 *
 * ★ supplier_id / product_id / sku_id 是 FK (proto3 raw int64 · 必填 · 须存在 · 否则下游 FK violation)。
 * ★ manufactured_at / expired_at 是 proto google.protobuf.Timestamp · protojson 收 RFC3339 string ·
 *    省略 = NULL。form 提交侧统一送 "YYYY-MM-DDT00:00:00Z" (UTC 零点 · 防本地时区把日期偏成前一天)。
 * ★ batch_no 工厂批号 (必填 · Unique(supplier_id, sku_id, batch_no) · 重复→OMS_MASTER_BATCH_NO_DUPLICATE)。
 * 返 BatchListItem (DTO 化精简 · 跟 GET single 同款 shape)。
 */
export interface CreateBatchRequest {
  supplier_id: number;
  product_id: number;
  sku_id: number;
  batch_no: string;
  manufactured_at?: string; // RFC3339 "YYYY-MM-DDT00:00:00Z" · 省略 = NULL
  expired_at?: string; // RFC3339 "YYYY-MM-DDT00:00:00Z" · 省略 = NULL
  remark?: string;
}

export async function listBatches(req: ListBatchesRequest): Promise<ListBatchesResponse> {
  const resp = await http.get<ListBatchesResponse>('/api/v1/master/batches', { params: req });
  return resp.data;
}

export async function getBatch(id: string | number): Promise<BatchListItem> {
  const resp = await http.get<BatchListItem>(`/api/v1/master/batches/${id}`);
  return resp.data;
}

export async function createBatch(req: CreateBatchRequest): Promise<BatchListItem> {
  // batch 无 enum 字段 · 日期已在 form 侧转 RFC3339 · 直接 POST raw body (protojson 照收)
  const resp = await http.post<BatchListItem>('/api/v1/master/batches', req);
  return resp.data;
}

// =========== CoaReport (coa-slice · list-by-batch / get / create · 履约收货 qc_pass 前置) ===========
// SSOT: api/proto/master/v1/master.proto:CreateCoaReportRequest / ListCoaReportsByBatchRequest
//
// ★ 文件上传 (uploadDocument) 是 platform 域 · 在 api/platform.ts · COA form 两步：
//   ① uploadDocument(file) → {DocID, Sha256} ② createCoaReport({batch_id, doc_id, sha256, file_name, …})。
// ★ uploaded_by 由 gateway 从 JWT 注入 · 前端不传 (防伪造)。tenant_id 由 gateway 从 ctx 注入。

/** ListCoaReportsRequest · GET /master/coa-reports?batch_id=… (batch_id 必填 · 后端强制 ListCoaReportsByBatch)。 */
export interface ListCoaReportsRequest {
  batch_id: number;
  page?: number;
  size?: number;
}

export type ListCoaReportsResponse = ListWrap<CoaReportListItem>;

/**
 * CreateCoaReportRequest · POST /master/coa-reports 请求体
 * SSOT: api/proto/master/v1/master.proto:CreateCoaReportRequest (gateway 注入 tenant_id/uploaded_by)
 *
 * ★ doc_id / sha256 / file_name 来自第①步 uploadDocument 回填 (隐藏字段)。
 * ★ valid_from / valid_to 是 proto Timestamp · protojson 收 RFC3339 · 省略 = NULL ·
 *    form 提交侧统一送 "YYYY-MM-DDT00:00:00Z" (UTC 零点 · 防时区偏移 · 同 batch)。
 */
export interface CreateCoaReportRequest {
  batch_id: number;
  doc_id: number;
  file_name: string;
  sha256: string;
  valid_from?: string; // RFC3339 "YYYY-MM-DDT00:00:00Z" · 省略 = NULL
  valid_to?: string; // RFC3339 "YYYY-MM-DDT00:00:00Z" · 省略 = NULL
  remark?: string;
}

export async function listCoaReports(req: ListCoaReportsRequest): Promise<ListCoaReportsResponse> {
  const resp = await http.get<ListCoaReportsResponse>('/api/v1/master/coa-reports', {
    params: req,
  });
  return resp.data;
}

export async function getCoaReport(id: string | number): Promise<CoaReportListItem> {
  const resp = await http.get<CoaReportListItem>(`/api/v1/master/coa-reports/${id}`);
  return resp.data;
}

export async function createCoaReport(req: CreateCoaReportRequest): Promise<CoaReportListItem> {
  // 日期已在 form 侧转 RFC3339 · uploaded_by/tenant_id 由 gateway 注入 · 直接 POST raw body (protojson 照收)
  const resp = await http.post<CoaReportListItem>('/api/v1/master/coa-reports', req);
  return resp.data;
}

// =========== ContactPerson (loose-ends ② PO 发送收件人选择 + GL-13 联系人管理 CRUD) ===========

export interface ListContactPersonsRequest {
  page?: number;
  size?: number;
  /** GL-13 · gateway 已透传 owner_kind ("customer"/"supplier" · 省略 = 不过滤 · 兼容既有调用)。 */
  owner_kind?: 'customer' | 'supplier';
}

export type ListContactPersonsResponse = ListWrap<ContactPersonListItem>;

/**
 * listContactPersons · GET /master/contact-persons (gateway passthrough · perm contact_person:read)
 * ★ GL-13 起 gateway 透传 owner_kind (proto ListContactPersonsRequest 有该字段) ·
 *   但 proto **无 owner_id 字段** (master.proto:748-754) → 归属 id 过滤
 *   (OwnerID===customerId/supplierId) 仍由调用方前端做 (联系人量小 · size=500 全拉 ·
 *   gateway ParsePageSize 上限 200)。PO 发送 Modal 既有调用 (无参) 行为不变。
 */
export async function listContactPersons(
  req: ListContactPersonsRequest = {},
): Promise<ListContactPersonsResponse> {
  const resp = await http.get<ListContactPersonsResponse>('/api/v1/master/contact-persons', {
    params: { page: 1, size: 500, ...req },
  });
  return resp.data;
}

/**
 * CreateContactPersonRequest · POST /master/contact-persons 请求体 (GL-13)
 * SSOT: api/proto/master/v1/master.proto:CreateContactPersonRequest (11 字段 · raw ·
 *       tenant_id 由 gateway 注入 · owner_kind/status 提交前 adapter 映射 proto 枚举值名)
 *
 * ★ owner_kind/owner_id 从 detail 上下文注入 (表单不可改) · owner_id 须真实存在 (FK → 422)。
 * ★ phone / mobile 明文入参 · master usecase 走 internal/crypto 加密落库 (现 Noop) ·
 *   response 永不回显 (proto ContactPerson 故意省略 · write-only)。
 * ★ status 必填 (UNSPECIFIED → 422) · create 恒送 'active'。
 * ★ email_status / email_bounce_count 不可提交 (平台回调维护 · proto request 无此字段)。
 */
export interface CreateContactPersonRequest {
  owner_kind: 'customer' | 'supplier';
  owner_id: number;
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  primary?: boolean;
  status: 'active' | 'disabled';
  remark?: string;
}

/**
 * UpdateContactPersonRequest · PATCH /master/contact-persons/{id} 请求体 (GL-13 + GL-10)
 * SSOT: api/proto/master/v1/master.proto:UpdateContactPersonRequest (id 走 path · gateway 覆盖)
 *
 * ★ 下游语义 = **全量替换** (master usecase 覆写全部业务字段 · email_status/bounce 除外):
 *   字段缺省 = proto3 零值 = 置空 · 不是 "不改"。调用方须回填整行 (ContactsPanel 从 row 取)。
 * ★ phone/mobile 前端拿不回原值 (proto 不暴露 · server 端加密落库)。GL-10 修 footgun:
 *   preserve_phone / preserve_mobile = true → 后端忽略对应入参 · 保留库中现值不重加密
 *   (编辑/停用未触碰该输入框时送 true · 防全量替换误置空); false (缺省) = 原全量替换语义
 *   (空串 = 显式置空)。
 * ★ 部署耦合: gateway protojson DiscardUnknown:false 严格解码 → 旧 gateway 收到
 *   preserve_* 字段会 400 · web 须与 ≥GL-10 的 gateway 一起上线。
 */
export interface UpdateContactPersonRequest {
  owner_kind: 'customer' | 'supplier';
  owner_id: number;
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  primary?: boolean;
  status: 'active' | 'disabled';
  remark?: string;
  /** GL-10 · true = 忽略 phone 入参 · 保留库中现值 (proto field 13)。 */
  preserve_phone?: boolean;
  /** GL-10 · true = 忽略 mobile 入参 · 保留库中现值 (proto field 14)。 */
  preserve_mobile?: boolean;
}

/** getContactPerson · GET /master/contact-persons/{id} (GL-13 · perm contact_person:read)。 */
export async function getContactPerson(id: string | number): Promise<ContactPersonListItem> {
  const resp = await http.get<ContactPersonListItem>(`/api/v1/master/contact-persons/${id}`);
  return resp.data;
}

/** createContactPerson · POST /master/contact-persons (GL-13 · perm contact_person:write)。 */
export async function createContactPerson(
  req: CreateContactPersonRequest,
): Promise<ContactPersonListItem> {
  const wire = {
    ...req,
    owner_kind: mapEnum(ContactOwnerKindToProto, req.owner_kind),
    status: mapEnum(ContactStatusToProto, req.status),
  };
  const resp = await http.post<ContactPersonListItem>('/api/v1/master/contact-persons', wire);
  return resp.data;
}

/**
 * updateContactPerson · PATCH /master/contact-persons/{id} (GL-13 · perm contact_person:write)。
 * ★ 全量替换语义 (见 UpdateContactPersonRequest 注) · 停用/启用 = 回填整行 + status 翻转 +
 *   preserve_phone/preserve_mobile=true (GL-10 · 保留已存加密值)。
 */
export async function updateContactPerson(
  id: string | number,
  req: UpdateContactPersonRequest,
): Promise<ContactPersonListItem> {
  const wire = {
    ...req,
    owner_kind: mapEnum(ContactOwnerKindToProto, req.owner_kind),
    status: mapEnum(ContactStatusToProto, req.status),
  };
  const resp = await http.patch<ContactPersonListItem>(
    `/api/v1/master/contact-persons/${id}`,
    wire,
  );
  return resp.data;
}

// =========== Pricing (P2-1 · 定价三层接线 · 建议价查询 + 客户折扣 5 endpoint) ===========

export interface GetPriceQuoteRequest {
  customer_id: number;
  sku_id: number;
  /** numeric 字符串 · 正数 (跟行项 qty 同形态) */
  qty: string;
}

/**
 * getPriceQuote · GET /master/price-quote (perm pricing:read)
 *
 * master GetPriceForCustomerSku passthrough (价表 base → tier 阶梯 → 客户折扣 → 货币精度收尾)。
 * ★ _silentError: 建议价是背景增强 · 查不到价 (无活跃价表 / OMS_MASTER_PRICE_NOT_AVAILABLE 等)
 *   不弹全局红 toast · 调用方 catch 后静默降级 (不填不报错)。
 */
export async function getPriceQuote(req: GetPriceQuoteRequest): Promise<PriceQuoteView> {
  const resp = await http.get<PriceQuoteView>('/api/v1/master/price-quote', {
    params: req,
    _silentError: true,
  });
  return resp.data;
}

export interface ListCustomerDiscountsRequest {
  page?: number;
  size?: number;
  customer_id?: number;
  product_id?: number;
  sku_id?: number;
}

export type ListCustomerDiscountsResponse = ListWrap<CustomerDiscountListItem>;

/**
 * CreateCustomerDiscountRequest · POST /master/customer-discounts 请求体
 * SSOT: api/proto/master/v1/master.proto:CreateCustomerDiscountRequest
 *
 * 字段约束 (跟 backend proto):
 *   - product_id / sku_id 0 = NULL · 互斥 (二者最多填一 · 都空 = 客户全局折扣)
 *   - discount_value numeric(18,6) 字符串 · percent 0.0500=5% / absolute 单价直减金额
 *   - valid_from / valid_to 是 protojson Timestamp → RFC3339 ("YYYY-MM-DDT00:00:00Z" ·
 *     照 Batch date 范式防时区偏移) · valid_to 省略 = 无截止
 */
export interface CreateCustomerDiscountRequest {
  customer_id: number;
  product_id?: number; // 0 = NULL
  sku_id?: number; // 0 = NULL
  discount_type: DiscountType;
  discount_value: string;
  valid_from: string; // "YYYY-MM-DD" (adapter 转 RFC3339)
  valid_to?: string; // "" = 无截止
  status: CustomerDiscountStatus;
  remark?: string;
}

export type UpdateCustomerDiscountRequest = CreateCustomerDiscountRequest;

/** dateToWireTs · "YYYY-MM-DD" → protojson Timestamp RFC3339 (T00:00:00Z · Batch date 范式)。 */
function dateToWireTs(d: string): string {
  return `${d}T00:00:00Z`;
}

export async function listCustomerDiscounts(
  req: ListCustomerDiscountsRequest,
): Promise<ListCustomerDiscountsResponse> {
  const resp = await http.get<ListCustomerDiscountsResponse>('/api/v1/master/customer-discounts', {
    params: req,
  });
  return resp.data;
}

export async function getCustomerDiscount(
  id: string | number,
): Promise<CustomerDiscountListItem> {
  const resp = await http.get<CustomerDiscountListItem>(
    `/api/v1/master/customer-discounts/${id}`,
  );
  return resp.data;
}

function customerDiscountToWire(req: CreateCustomerDiscountRequest) {
  return {
    customer_id: req.customer_id,
    ...(req.product_id ? { product_id: req.product_id } : {}),
    ...(req.sku_id ? { sku_id: req.sku_id } : {}),
    discount_type: mapEnum(DiscountTypeToProto, req.discount_type),
    discount_value: req.discount_value,
    valid_from: dateToWireTs(req.valid_from),
    ...(req.valid_to ? { valid_to: dateToWireTs(req.valid_to) } : {}),
    status: mapEnum(CustomerDiscountStatusToProto, req.status),
    ...(req.remark?.trim() ? { remark: req.remark.trim() } : {}),
  };
}

export async function createCustomerDiscount(
  req: CreateCustomerDiscountRequest,
): Promise<CustomerDiscountListItem> {
  const resp = await http.post<CustomerDiscountListItem>(
    '/api/v1/master/customer-discounts',
    customerDiscountToWire(req),
  );
  return resp.data;
}

export async function updateCustomerDiscount(
  id: string | number,
  req: UpdateCustomerDiscountRequest,
): Promise<CustomerDiscountListItem> {
  const resp = await http.put<CustomerDiscountListItem>(
    `/api/v1/master/customer-discounts/${id}`,
    customerDiscountToWire(req),
  );
  return resp.data;
}

/**
 * disableCustomerDiscount · DELETE /master/customer-discounts/{id}
 * 语义: soft-disable · backend status → 'disabled' (terminal · 与 price_list deprecate 平行)
 */
export async function disableCustomerDiscount(id: string | number): Promise<void> {
  await http.delete(`/api/v1/master/customer-discounts/${id}`);
}
